import * as React from 'react';
import { ErrorInfo } from 'react';

export interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  errorInfo?: ErrorInfo;
}

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
  level?: 'page' | 'section' | 'component';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  const isDevelopment = process.env['NODE_ENV'] === 'development';

  return (
    <div className="min-h-[200px] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <h2 className="text-2xl font-bold text-error mb-4">Something went wrong</h2>
        <p className="text-base-content/70 mb-6">
          We're sorry, but something unexpected happened. Please try again.
        </p>

        {isDevelopment && error && (
          <details className="mb-6 text-left">
            <summary className="cursor-pointer text-sm text-base-content/70 hover:text-base-content">
              Error details
            </summary>
            <pre className="mt-2 p-4 bg-base-200 rounded text-xs overflow-auto max-h-64">
              {error.stack || error.toString()}
            </pre>
          </details>
        )}

        <button onClick={resetErrorBoundary} className="btn btn-primary">
          Try Again
        </button>
      </div>
    </div>
  );
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private resetTimeoutId: NodeJS.Timeout | null = null;
  private previousResetKeys: Array<string | number> = [];

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Call error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // Log error in development
    if (process.env['NODE_ENV'] === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset on resetKeys change
    if (hasError && resetKeys) {
      const hasResetKeyChanged = resetKeys.some(
        (key, index) => key !== this.previousResetKeys[index]
      );

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset on any props change if enabled
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }

    this.previousResetKeys = resetKeys || [];
  }

  override componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  override render() {
    const { hasError, error, errorInfo } = this.state;
    const { fallback: Fallback = DefaultErrorFallback, children, isolate, level } = this.props;

    if (hasError && error) {
      const errorBoundaryClass = isolate
        ? 'error-boundary-isolated'
        : `error-boundary-${level || 'component'}`;

      return (
        <div className={errorBoundaryClass}>
          <Fallback
            error={error}
            {...(errorInfo ? { errorInfo } : {})}
            resetErrorBoundary={this.resetErrorBoundary}
          />
        </div>
      );
    }

    return children;
  }
}
